import type { InferResponseType } from 'hono/client';
import { type ApiClient, useFetch } from '../api';

type GetRolloutWindows =
  ApiClient['v1']['workspaces'][':workspaceId']['devices'][':deviceId']['rollout-windows']['$get'];
export type RolloutWindowListResponse = InferResponseType<GetRolloutWindows, 200>;
export type RolloutWindowResponse = RolloutWindowListResponse['rolloutWindows'][number];
export type RolloutWindowType = RolloutWindowResponse['type'];

const ROLLOUT_WINDOW_PAGE_SIZE = 100;

export function useRolloutWindows(workspaceId: number, deviceId: number) {
  return useFetch<RolloutWindowListResponse>(
    `workspaces/${workspaceId}/devices/${deviceId}/rollout-windows`,
    (client) =>
      client.v1.workspaces[':workspaceId'].devices[':deviceId']['rollout-windows'].$get({
        param: { workspaceId: workspaceId.toString(), deviceId: deviceId.toString() },
        query: {},
      })
  );
}

export function useOsRolloutWindows(workspaceId: number, deviceId: number, enabled: boolean) {
  return useFetch<RolloutWindowListResponse>(
    enabled ? `workspaces/${workspaceId}/devices/${deviceId}/os-rollout-windows` : null,
    async (client) => {
      let offset = 0;
      const rolloutWindows: RolloutWindowResponse[] = [];

      while (true) {
        const response = await client.v1.workspaces[':workspaceId'].devices[':deviceId'][
          'rollout-windows'
        ].$get({
          param: { workspaceId: workspaceId.toString(), deviceId: deviceId.toString() },
          query: { type: 'APPLICATION_AND_OS', limit: ROLLOUT_WINDOW_PAGE_SIZE, offset },
        });
        if (!response.ok) return response;

        const page = (await response.clone().json()) as RolloutWindowListResponse;
        rolloutWindows.push(...page.rolloutWindows);

        const hasActiveWindow = page.rolloutWindows.some((window) => window.status === 'ACTIVE');
        const hasMorePages =
          page.rolloutWindows.length > 0 && offset + page.rolloutWindows.length < page.itemCount;
        if (hasActiveWindow || !hasMorePages) {
          return new Response(
            JSON.stringify({
              ...page,
              rolloutWindows,
              limit: rolloutWindows.length,
              offset: 0,
            }),
            { headers: { 'Content-Type': 'application/json' } }
          );
        }

        offset += page.rolloutWindows.length;
      }
    },
    { showForbiddenErrorToast: false }
  );
}
